"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  INPUT,
  LABEL,
  RESULT_CARD,
  SELECT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  corePoints,
  IB_CORE_GRADES,
  type IbCoreGrade,
  type IbLevel,
  type IbResult,
  ibResult,
  isDiplomaCombination,
} from "@/lib/tool-kit/grades/ib";

/** English strings. A translated content file overrides these through `ui`. */
export const IB_SCORE_CALCULATOR_STRINGS = {
  subjectsHeading: "Subjects",
  subjectLabel: "Subject",
  subjectPlaceholder: "Subject {n}",
  levelLabel: "Level",
  levels: {
    HL: "Higher level (HL)",
    SL: "Standard level (SL)",
  },
  gradeLabel: "Grade (1–7)",
  coreHeading: "Core",
  tokLabel: "Theory of knowledge grade",
  eeLabel: "Extended essay grade",
  casLabel: "CAS requirements completed",
  results: "Result",
  total: "Total points",
  totalOf: "{total} of 45",
  subjectPoints: "Subject points",
  corePoints: "TOK and EE points",
  coreFailing: "Failing condition",
  hlPoints: "HL points (12 needed)",
  slPoints: "SL points ({min} needed)",
  awarded: "Diploma requirements met: no failing condition applies.",
  notAwarded: "Diploma not awarded — failing conditions:",
  failures: {
    cas: "CAS requirements are not met.",
    total: "The total is below 24 points.",
    "core-e":
      "An E in theory of knowledge or the extended essay is a failing condition.",
    "grade-1": "A grade 1 in any subject is a failing condition.",
    "grade-2": "Grade 2 appears three or more times.",
    "grade-3": "Grade 3 or below appears four or more times.",
    hl: "HL subjects total fewer than 12 points (with four HL subjects, the best three count).",
    sl: "SL subjects total fewer than {min} points.",
  },
  combination:
    "A diploma takes three or four subjects at HL and the rest at SL. HL subjects selected now: {hl}.",
  notModelled:
    "Not checked here: an N (no grade) in any component, or a penalty for academic misconduct — both also stop the diploma.",
  matrixHeading: "TOK and EE points matrix",
  matrixCorner: "EE ↓ · TOK →",
  matrixFailing: "Fail",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "IB Diploma: {total} points ({subjects} subject + {core} core)",
} as const;

type Strings = typeof IB_SCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface SubjectRow {
  id: number;
  name: string;
  level: IbLevel;
  grade: string;
}

const INITIAL_SUBJECTS: SubjectRow[] = [
  { id: 1, name: "", level: "HL", grade: "6" },
  { id: 2, name: "", level: "HL", grade: "6" },
  { id: 3, name: "", level: "HL", grade: "5" },
  { id: 4, name: "", level: "SL", grade: "6" },
  { id: 5, name: "", level: "SL", grade: "5" },
  { id: 6, name: "", level: "SL", grade: "5" },
];

const SUBJECT_GRADES = ["7", "6", "5", "4", "3", "2", "1"] as const;

function CoreMatrix({
  tok,
  ee,
  strings,
}: {
  tok: IbCoreGrade;
  ee: IbCoreGrade;
  strings: Strings;
}) {
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.matrixHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.matrixCorner}
              </th>
              {IB_CORE_GRADES.map((grade) => (
                <th className={TH} key={grade} scope="col">
                  {grade}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {IB_CORE_GRADES.map((row) => (
              <tr key={row}>
                <th className={TD} scope="row">
                  {row}
                </th>
                {IB_CORE_GRADES.map((column) => {
                  const points = corePoints(column, row);
                  const current = row === ee && column === tok;
                  return (
                    <td
                      className={`${TD} ${current ? "bg-primary/10 font-semibold" : ""}`}
                      key={column}
                    >
                      {points === null ? strings.matrixFailing : points}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ResultPanel({
  result,
  strings,
  fmt,
}: {
  result: IbResult;
  strings: Strings;
  fmt: Format;
}) {
  const core = result.core === null ? strings.coreFailing : fmt(result.core);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.total}
          value={t(strings.totalOf, { total: fmt(result.total) })}
        />
        <StatCard
          label={strings.subjectPoints}
          value={fmt(result.subjectPoints)}
        />
        <StatCard label={strings.corePoints} value={core} />
        <StatCard label={strings.hlPoints} value={fmt(result.hlPoints)} />
        <StatCard
          label={t(strings.slPoints, { min: fmt(result.slMinimum) })}
          value={fmt(result.slPoints)}
        />
      </StatGrid>
      {result.failures.length === 0 ? (
        <p className="font-medium text-foreground text-sm">{strings.awarded}</p>
      ) : (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.notAwarded}
          </p>
          <ul className="list-disc space-y-1 ps-5 text-foreground text-sm">
            {result.failures.map((failure) => (
              <li key={failure}>
                {t(strings.failures[failure], { min: fmt(result.slMinimum) })}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-muted-foreground text-xs">{strings.notModelled}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          total: fmt(result.total),
          subjects: fmt(result.subjectPoints),
          core,
        })}
      />
    </>
  );
}

export default function IbScoreCalculatorWidget() {
  const strings = useToolUi(IB_SCORE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [subjects, setSubjects] = useState<SubjectRow[]>(INITIAL_SUBJECTS);
  const [tok, setTok] = useState<IbCoreGrade>("B");
  const [ee, setEe] = useState<IbCoreGrade>("B");
  const [cas, setCas] = useState(true);

  const parsed = subjects.map((row) => ({
    level: row.level,
    grade: Number(row.grade),
  }));
  const combination = isDiplomaCombination(parsed);
  const result = ibResult(parsed, tok, ee, cas);
  const coreOptions = IB_CORE_GRADES.map((grade) => ({
    value: grade,
    label: grade,
  }));

  function update(id: number, patch: Partial<SubjectRow>) {
    setSubjects((all) =>
      all.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.subjectsHeading}</h3>
        {subjects.map((row, index) => (
          <div className="flex flex-wrap items-end gap-3" key={row.id}>
            <div className="min-w-0 flex-1 basis-40 space-y-1">
              <label className={LABEL} htmlFor={`ibc-name-${row.id}`}>
                {strings.subjectLabel}
              </label>
              <input
                className={INPUT}
                id={`ibc-name-${row.id}`}
                onChange={(event) =>
                  update(row.id, { name: event.target.value })
                }
                placeholder={t(strings.subjectPlaceholder, { n: index + 1 })}
                type="text"
                value={row.name}
              />
            </div>
            <div className="w-48 space-y-1">
              <label className={LABEL} htmlFor={`ibc-level-${row.id}`}>
                {strings.levelLabel}
              </label>
              <select
                className={SELECT}
                id={`ibc-level-${row.id}`}
                onChange={(event) =>
                  update(row.id, { level: event.target.value as IbLevel })
                }
                value={row.level}
              >
                <option value="HL">{strings.levels.HL}</option>
                <option value="SL">{strings.levels.SL}</option>
              </select>
            </div>
            <div className="w-28 space-y-1">
              <label className={LABEL} htmlFor={`ibc-grade-${row.id}`}>
                {strings.gradeLabel}
              </label>
              <select
                className={SELECT}
                id={`ibc-grade-${row.id}`}
                onChange={(event) =>
                  update(row.id, { grade: event.target.value })
                }
                value={row.grade}
              >
                {SUBJECT_GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.coreHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ibc-tok"
            label={strings.tokLabel}
            onChange={setTok}
            options={coreOptions}
            value={tok}
          />
          <SelectField
            id="ibc-ee"
            label={strings.eeLabel}
            onChange={setEe}
            options={coreOptions}
            value={ee}
          />
        </div>
        <Checkbox checked={cas} label={strings.casLabel} onChange={setCas} />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {combination ? (
          <ResultPanel fmt={fmt} result={result} strings={strings} />
        ) : (
          <Notice>{t(strings.combination, { hl: fmt(result.hlCount) })}</Notice>
        )}
      </section>

      <CoreMatrix ee={ee} strings={strings} tok={tok} />
    </div>
  );
}
